// systems/drops.js
// Centralized drop handling with corruption support

import { gameState } from '../game/gameState.js';
import { SKILLS, GAME_CONFIG } from '../game/constants.js';
import { showItemGain } from '../ui/floatingNotifications.js';
import { getCorruptionLootMultiplier, isSkillCorrupted, isCorruptibleSkill } from './corruption.js';
import { getTalentLootBonus, getTalentFlatBonus } from './talentTree/talentTree.js';
import { bountySkill } from '../skills/bounty/bounty.js';

/**
 * Award drops to the player with corruption loot multiplier applied
 * This is the central place to apply all loot multipliers (corruption, talents, etc.)
 *
 * @param {string} skillId - The skill awarding the drops
 * @param {Array} drops - Array of { itemId, quantity } objects
 * @param {Object} options - Optional settings
 * @param {boolean} options.showNotification - Whether to show floating notification (default: true)
 * @returns {Object} - { totalItems, corrupted, inventoryFull }
 */
export function awardDrops(skillId, drops, options = {}) {
    const { showNotification = true } = options;

    // Calculate total loot multiplier (corruption + future talents)
    let lootMultiplier = 1;
    let corrupted = false;

    if (isCorruptibleSkill(skillId)) {
        // Corruption multiplier
        const corruptionMult = getCorruptionLootMultiplier(skillId);
        lootMultiplier *= corruptionMult;
        corrupted = isSkillCorrupted(skillId);
    }

    // Apply talent loot bonus (always, not just corruptible skills)
    const talentLootBonus = getTalentLootBonus(skillId);
    if (talentLootBonus > 0) {
        lootMultiplier *= (1 + talentLootBonus);
    }

    let totalItems = 0;
    let inventoryFull = false;

    for (const drop of drops) {
        // Apply Contract Momentum bonus per-item (+15% for active contract items)
        let itemMultiplier = lootMultiplier;
        const momentumBonus = bountySkill.getContractMomentumBonus(drop.itemId);
        if (momentumBonus > 0) {
            itemMultiplier *= (1 + momentumBonus);
        }

        let finalQuantity = Math.floor(drop.quantity * itemMultiplier);
        // Apply Coin Magnet talent (+10% coins) for coin drops
        if (drop.itemId === 'coins') {
            const coinBonus = getTalentFlatBonus(SKILLS.PILFERING, 'globalCoinBonus') || 0;
            if (coinBonus > 0) {
                finalQuantity = Math.floor(finalQuantity * (1 + coinBonus));
            }
        }
        const result = gameState.addItem(drop.itemId, finalQuantity);

        if (result.success && result.added > 0) {
            totalItems += result.added;
            if (showNotification) {
                showItemGain(drop.itemId, result.added, corrupted);
            }
            // Track progress for bounty contracts
            bountySkill.trackItemProgress(drop.itemId, result.added);
        }

        // Track if any drop failed or was partial due to full inventory
        if (!result.success || result.added < finalQuantity) {
            inventoryFull = true;
        }
    }

    return { totalItems, corrupted, inventoryFull };
}

/**
 * Award a single drop to the player with corruption loot multiplier applied
 * @param {string} skillId - The skill awarding the drop
 * @param {string} itemId - The item to award
 * @param {number} quantity - Base quantity
 * @param {Object} options - Optional settings
 * @returns {Object} - { finalQuantity, corrupted }
 */
export function awardDrop(skillId, itemId, quantity, options = {}) {
    const result = awardDrops(skillId, [{ itemId, quantity }], options);
    return {
        finalQuantity: result.totalItems,
        corrupted: result.corrupted
    };
}

/**
 * Select a weighted drop from a drop table
 * @param {Array} drops - Array of { itemId, quantity, weight } objects
 * @returns {Object} - Selected drop { itemId, quantity }
 */
export function selectWeightedDrop(drops) {
    // If only one drop or no weights, return first drop
    if (drops.length === 1 || drops.every(d => d.weight === 1)) {
        return drops[0];
    }

    // Calculate total weight
    const totalWeight = drops.reduce((sum, drop) => sum + (drop.weight || 1), 0);

    // Random roll
    let roll = Math.random() * totalWeight;

    // Find the drop
    for (const drop of drops) {
        roll -= (drop.weight || 1);
        if (roll <= 0) {
            return drop;
        }
    }

    // Fallback to first drop
    return drops[0];
}

/**
 * Check if an item can fit in inventory (has empty slot OR existing stack with room)
 * @param {string} itemId - The item to check
 * @returns {boolean} - Whether the item can fit
 */
export function canFitItem(itemId) {
    // Check if item exists in inventory with room to stack
    const existingSlot = gameState.findInventorySlot(itemId);
    if (existingSlot !== -1) {
        const slot = gameState.player.inventorySlots[existingSlot];
        if (slot.quantity < GAME_CONFIG.MAX_STACK) {
            return true;
        }
    }
    // Check if there's an empty slot
    return gameState.hasInventorySpace();
}

export default {
    awardDrops,
    awardDrop,
    selectWeightedDrop,
    canFitItem,
};
